import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiMusic, FiPlay, FiTrendingUp, FiCheckCircle, FiGlobe, FiAward, FiArrowLeft } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import MediaLogos from './MediaLogos';

const SeoPage = () => {
  const { slug } = useParams();
  
  // Convert slug back to readable title (approximate)
  const topic = slug 
    ? slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) 
    : "Afrobeats Music";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Dynamic Content Generation
  const description = `Discover everything about ${topic} and how Waconzy's unique blend of Highlife and Afrobeats fits into the conversation. Stream hits like "I Celebrate" and "Jara".`;

  return (
    <div className="bg-black text-white min-h-screen pt-24 pb-20">
      <Helmet>
        <title>{`${topic} | Waconzy Official - The Lion of Afrobeats`}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={`${topic}, Waconzy, Afrobeats, Nigerian Music, I Celebrate, Jara`} />
        <link rel="canonical" href={`https://waconzy.com/topic/${slug}`} />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4">
        {/* Breadcrumb */}
        <Link to="/topics" className="flex items-center gap-2 text-yellow-500 hover:text-white mb-8 transition-colors">
          <SafeIcon icon={FiArrowLeft} /> Back to Topics
        </Link>

        {/* Header */}
        <header className="mb-12 border-b border-zinc-800 pb-8">
          <span className="bg-gradient-to-r from-red-600 to-red-500 text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4 inline-block">
             Music Insights
          </span>
          <h1 className="text-4xl md:text-6xl font-black mb-6 capitalize leading-tight">
            {topic}: <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-green-500">The Ultimate Guide</span>
          </h1>
          <p className="text-xl text-gray-400 leading-relaxed">
            Exploring the world of <strong>{topic}</strong> through the lens of a global Afrobeats icon.
          </p>
        </header>

        {/* Main Content Area */}
        <article className="prose prose-lg prose-invert max-w-none">
          
          {/* Section 1: Introduction */}
          <div className="bg-zinc-900/50 p-8 rounded-3xl border border-zinc-800 mb-10">
            <h2 className="text-2xl font-bold text-white mb-4">Understanding {topic}</h2>
            <p className="text-gray-300 mb-6">
              When discussing <strong>{topic}</strong>, it is impossible to ignore the massive cultural shift happening in the global music landscape. 
              From the streets of Port Harcourt to the stages of London and New York, the sound has evolved into a global phenomenon. 
              Artists like <a href="https://waconzy.com" className="text-green-500 hover:underline font-bold">Waconzy</a> have been instrumental in this evolution, 
              bridging the gap between traditional African rhythms and contemporary pop production.
            </p>
            <p className="text-gray-300">
              Whether you are a long-time fan searching for <em>{topic}</em> or a newcomer exploring the genre, the impact of songs like "I Celebrate" serves as a perfect entry point.
            </p>
          </div>

          {/* Section 2: The Waconzy Connection */}
          <div className="grid md:grid-cols-2 gap-8 mb-10">
            <div className="bg-gradient-to-br from-black to-zinc-900 p-8 rounded-3xl border border-yellow-500/30">
              <SafeIcon icon={FiMusic} className="text-4xl text-yellow-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">The "I Celebrate" Legacy</h3>
              <p className="text-gray-400 text-sm mb-4">
                If you love <strong>{topic}</strong>, you likely appreciate music that uplifts and inspires. Waconzy's "I Celebrate" is the quintessential success anthem, perfectly aligning with the positive vibes associated with this genre.
              </p>
              <a href="https://waconzy.com" className="text-yellow-500 font-bold text-sm hover:text-white transition-colors">
                Visit Waconzy.com &rarr;
              </a>
            </div>
            <div className="bg-gradient-to-br from-black to-zinc-900 p-8 rounded-3xl border border-green-500/30">
              <SafeIcon icon={FiTrendingUp} className="text-4xl text-green-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">Viral Hits & {topic}</h3>
              <p className="text-gray-400 text-sm mb-4">
                The recent viral success of "Jara" on TikTok demonstrates how <strong>{topic}</strong> continues to dominate social media trends. The blend of Highlife and Amapiano creates an irresistible sound.
              </p>
              <a href="https://waconzy.com" className="text-green-500 font-bold text-sm hover:text-white transition-colors">
                Stream on Waconzy.com &rarr;
              </a>
            </div>
          </div>

          {/* Section 3: Deep Dive */}
          <h2 className="text-3xl font-black text-white mb-6">Why {topic} Matters in 2025</h2>
          <p className="text-gray-300 mb-6">
            The year 2025 marks a turning point for <strong>{topic}</strong>. We are seeing a fusion of sounds that defies borders. 
            For enthusiasts of <a href="https://waconzy.com" className="text-red-500 hover:underline">Waconzy</a> and similar artists, this era represents the golden age of creativity. 
            The lyrical depth found in Waconzy’s discography—covering themes of celebration, hustle, and gratitude—resonates deeply with the core audience of {topic}.
          </p>

          {/* Key Takeaways */}
          <div className="bg-zinc-800 p-6 rounded-xl mb-10">
            <h3 className="font-bold text-white mb-4">Key Highlights:</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" />
                <span className="text-gray-300"><strong>Authenticity:</strong> {topic} thrives on genuine storytelling, a hallmark of Waconzy's career.</span>
              </li>
              <li className="flex items-start gap-3">
                <SafeIcon icon={FiGlobe} className="text-blue-500 mt-1" />
                <span className="text-gray-300"><strong>Global Reach:</strong> Fans from Lagos to Los Angeles are searching for {topic}.</span>
              </li>
              <li className="flex items-start gap-3">
                <SafeIcon icon={FiAward} className="text-yellow-500 mt-1" />
                <span className="text-gray-300"><strong>Legacy:</strong> With over 50 awards, Waconzy stands as a pillar in the {topic} conversation.</span>
              </li>
            </ul>
          </div>

          {/* Call to Action */}
          <div className="bg-gradient-to-r from-red-600 to-purple-900 rounded-3xl p-10 text-center">
            <h3 className="text-3xl font-black text-white mb-4">Experience the Sound</h3>
            <p className="text-lg text-gray-200 mb-8 max-w-2xl mx-auto">
              Ready to dive deeper into <strong>{topic}</strong>? Listen to the curated Waconzy playlist that defines the genre.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://open.spotify.com/artist/3fECabmj2EOhc8imK4YKMm" target="_blank" rel="noreferrer" className="bg-white text-black px-8 py-3 rounded-full font-bold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2">
                <SafeIcon icon={FiMusic} /> Stream on Spotify
              </a>
              <a href="https://www.youtube.com/waconzy" target="_blank" rel="noreferrer" className="bg-black text-white px-8 py-3 rounded-full font-bold hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2">
                <SafeIcon icon={FiPlay} /> Watch on YouTube
              </a>
            </div>
          </div>

        </article>

        {/* Media Logos Reuse */}
        <div className="mt-20">
          <MediaLogos />
        </div>

      </div>
    </div>
  );
};

export default SeoPage;